import React from 'react'
import md5 from 'js-md5'
import {sanitizeTranslationHtml} from '../htmlSanitize.js'

# Global registry for string collection
export class I18nRegistry
  constructor: (@clientAPI) ->
    @pendingStrings = new Map()
    @registeredHashes = new Set()
    @currentContext = null
    @currentRoute = null
    @flushTimeout = null
    @appName = null

  setContext: (context, route, app) ->
    @currentContext = context
    @currentRoute = route
    @appName = app
    @loadCacheFromStorage()
    @loadSeenFromStorage()

  # Does this page render the AUTHORED text? That is the question every English
  # branch below is really asking, and it is not `locale == 'en'`: a browser
  # reports 'en-US', so that comparison sent every English visitor down the
  # translated path — catalogue cache, no seen-set (card 6aa6c73847c945dd8596e7d9).
  # The rule is shared/i18n/readLocale.coffee; no manager means no locale was
  # ever chosen, which is the source.
  readsSource: ->
    manager = window.i18nManager
    if manager? then manager.readsSource() else true

  loadCacheFromStorage: ->
    return if @readsSource()  # the authored text needs no translation cache
    locale = window.i18nManager.getLocale()

    cacheKey = "i18n_#{locale}_#{@appName}"
    try
      cached = localStorage.getItem(cacheKey)
      if cached
        window.i18nTranslationCache = JSON.parse(cached)
        # console.log "✅ Loaded #{Object.keys(window.i18nTranslationCache).length} cached translations from localStorage"
    catch e
      console.warn "Failed to load translation cache:", e

  # ── The English seen-set: why registration is not a per-page-view cost ──────
  #
  # @registeredHashes used to live only for the page load, and the ONLY thing
  # persisted was the translation cache — written "no caching for English". So
  # an English visitor re-registered every static string of every page on EVERY
  # view and the client POSTed i18nStrings/upsertBatch each time. The server
  # dedupes by md5 and inserts nothing, so the row count stayed right while the
  # request count tracked traffic: one public write-path POST per page view per
  # visitor, forever, for strings the instance has seen thousands of times
  # (card 6aa49493a68eb97372827dd3).
  #
  # Seeding @registeredHashes from localStorage makes the existing dedupe do the
  # whole job: nothing pends, so scheduleBatchFlush never fires and no POST goes
  # out until the page actually shows text this browser has not registered here
  # before.
  #
  # ENGLISH ONLY, and that is not a simplification. In another locale
  # i18nCreateElement/t() only reach registerString for a string with NO cached
  # translation, so the pending set is already exactly "what is still
  # untranslated" — and re-POSTing those is how the auto-translate-on-mint
  # contract lands (mint → translate → the NEXT load serves it). A seen-set
  # there would skip the POST that collects the translation and strand the
  # string in English permanently.
  #
  # The set expires (SEEN_TTL_MS) so a catalog that is wiped, republished or
  # re-pointed re-mints on its own instead of needing every visitor to clear
  # site data, and it is capped (SEEN_MAX) so a machine-generated page cannot
  # grow it without bound.
  seenKey: -> "i18n_seen_en_#{@appName}"

  SEEN_TTL_MS = 7 * 24 * 60 * 60 * 1000
  SEEN_MAX = 5000

  loadSeenFromStorage: ->
    return unless @readsSource()
    try
      raw = localStorage.getItem(@seenKey())
      return unless raw
      parsed = JSON.parse(raw)
      return unless parsed?.stamp and Array.isArray(parsed.hashes)
      if Date.now() - parsed.stamp > SEEN_TTL_MS
        localStorage.removeItem(@seenKey())
        return
      @registeredHashes.add(hash) for hash in parsed.hashes
    catch e
      # A corrupt or unreadable entry must never stop the page registering —
      # the cost of ignoring it is one ordinary POST.
      console.warn "Failed to load i18n seen-set:", e

  # Record hashes the server has CONFIRMED. Called from flush's success path
  # only: a failed POST leaves them unpersisted so the next load retries, while
  # @registeredHashes still suppresses a duplicate within this page load.
  saveSeenToStorage: (hashes) ->
    return unless hashes?.length
    return unless @readsSource()
    try
      existing = []
      raw = localStorage.getItem(@seenKey())
      if raw
        parsed = JSON.parse(raw)
        existing = parsed.hashes if Array.isArray(parsed?.hashes)
      merged = Array.from(new Set(existing.concat(hashes)))
      # Oldest first, so the cap drops the least recently minted.
      merged = merged.slice(merged.length - SEEN_MAX) if merged.length > SEEN_MAX
      localStorage.setItem(@seenKey(), JSON.stringify({stamp: Date.now(), hashes: merged}))
    catch e
      console.warn "Failed to save i18n seen-set:", e

  # Blacklist of technical terms and icon names
  BLACKLIST = [
    'navigate_before', 'navigate_next', 'insert_photo', 'menu', 'star', 'pause',
    'alarm', 'schedule', 'equalizer', 'close', 'check', 'arrow', 'search',
    'home', 'settings', 'info', 'warning', 'error', 'success',
    'lazy'
  ]

  registerString: (text) ->
    return if !text?.trim?()
    return if typeof text != 'string'

    trimmed = text.trim()

    # Skip blacklisted terms
    return if trimmed.toLowerCase() in BLACKLIST

    # Skip very short strings (likely names or abbreviations)
    return if trimmed.length < 2

    # Skip single lowercase words (likely technical terms or names)
    return if /^[a-z]+$/.test(trimmed) && trimmed.length < 5

    # Skip strings starting with numbers followed by a space or period (like "0 seconds", "1. item", "1.) item")
    # Allow ordinals like "1st Grade", "2nd Grade" etc.
    return if /^\d+[\s.]/.test(trimmed)

    # Skip hex strings (UUIDs, color codes, IDs)
    return if /^[a-f0-9]{6,}$/i.test(trimmed)

    # Skip strings with underscores (variable names, keys)
    return if /_/.test(trimmed)

    # Skip strings with dashes that have no spaces (CSS classes, kebab-case like "get-started-widget")
    # Allow dashes in normal sentences like "Step 1 - Enrollment Will Only Take A Minute"
    return if /-/.test(trimmed) && !/\s/.test(trimmed)

    # Skip paths (start with slash, or contain backslash, or contain double slash)
    return if /^\/|\\|\/\//.test(trimmed)

    # Skip URLs (contain :// or www.)
    return if /(:\/\/|www\.)/.test(trimmed)

    # Skip standalone email addresses (whole string is an email, not a sentence containing one)
    return if /^\S+@\S+\.\S+$/.test(trimmed)

    # Skip pure numbers, decimals, percentages
    return if /^[\d\.,\s%]+$/.test(trimmed)

    # Skip pure punctuation
    return if /^[^\w\s]+$/.test(trimmed)

    # Skip mostly punctuation (more than 50% non-word chars)
    wordChars = trimmed.match(/[\w\s]/g)?.length || 0
    return if wordChars / trimmed.length < 0.5

    # Only allow strings with mostly letters (at least 70% alphabetic)
    # BUT allow lower threshold for strings with {VAR} placeholders or parentheses
    letters = trimmed.match(/[a-zA-Z]/g)?.length || 0
    hasParensOrVar = trimmed.includes('{VAR}') || /\([^)]+\)/.test(trimmed) || /<\w/.test(trimmed)
    threshold = if hasParensOrVar then 0.5 else 0.65
    return if letters / trimmed.length < threshold

    # Create hash from text only (no context for simplicity and cost savings)
    hash = md5(text)

    # Skip if already registered in this session
    return if @registeredHashes.has(hash)

    # console.log "📝 Registering string: '#{text.substring(0, 50)}...'"

    @pendingStrings.set hash, {
      context: @currentContext
      text: text
      hash: hash
      app: @appName
      route: @currentRoute
    }

    @registeredHashes.add(hash)
    @scheduleBatchFlush()

  # For programmatic registration (e.g., from game instruction_set)
  registerProgrammatic: (text, app = @appName, route = @currentRoute) ->
    return if !text?.trim?()
    return if typeof text != 'string'

    # Create hash from text only (no context)
    hash = md5(text)

    # Skip if already registered
    return if @registeredHashes.has(hash)

    @pendingStrings.set hash, {
      context: context
      text: text
      hash: hash
      app: app
      route: route
    }

    @registeredHashes.add(hash)
    @scheduleBatchFlush()

    return hash  # Return hash for immediate lookup

  scheduleBatchFlush: ->
    clearTimeout(@flushTimeout) if @flushTimeout
    @flushTimeout = setTimeout (=> @flush()), 100

  flush: ->
    return if @pendingStrings.size == 0

    # Check if i18nString model is available
    if !@clientAPI.i18nString?
      console.warn "i18nString model not available in client API - strings not registered"
      @pendingStrings.clear()
      return

    strings = Array.from(@pendingStrings.values())
    @pendingStrings.clear()

    # console.log "Registering #{strings.length} i18n strings..."

    # Upsert to model (will auto-trigger translation)
    @clientAPI.i18nString.upsertBatch({strings: strings}).then (response) =>
      # Get current locale
      if @readsSource()
        # No TRANSLATION cache for English — but remember that the server has
        # these strings, so the next view of this page posts nothing at all.
        @saveSeenToStorage(s.hash for s in strings)
        return

      cacheKey = "i18n_#{window.i18nManager.getLocale()}_#{@appName}"

      # Load existing cache from localStorage
      cache = {}
      try
        cached = localStorage.getItem(cacheKey)
        cache = JSON.parse(cached) if cached
      catch e
        console.warn "Failed to load translation cache:", e

      # Merge new translations from response
      results = response?.data?.result?.results || []
      for result in results
        if result.text? && result.hash?
          cache[result.hash] = result.text

      # Save back to localStorage
      try
        localStorage.setItem(cacheKey, JSON.stringify(cache))
        # console.log "Cached #{Object.keys(cache).length} translations for #{locale}"
      catch e
        console.warn "Failed to save translation cache:", e

      # Set in global for i18nCreateElement access
      window.i18nTranslationCache = cache
    .catch (err) ->
      console.error "Failed to register i18n strings:", err

# Create singleton
window.i18nRegistry = null

# Custom JSX factory with translation lookup
export i18nCreateElement = (type, props, ...children) ->
  # Check if element has complete HTML content to translate
  if props?['___i18n']?
    htmlContent = props['___i18n']
    # console.log "🔍 Found ___i18n:", htmlContent.substring(0, 50)
    hash = md5(htmlContent)

    # Look up translation
    translated = window.i18nTranslationCache?[hash]

    # Remove ___i18n prop
    newProps = {...props}
    delete newProps['___i18n']

    if translated
      # Nested static markup (e.g. "Get <strong class='hi'>started</strong> now")
      # must SURVIVE translation — the old path stripped every tag and rendered
      # flat, unstyled text in non-en locales (the attr-parity breakage class).
      # Decided 2026-07-08: when the source content carries markup and there is
      # no {VAR} interleaving, insert the translated HTML directly. Trust model
      # unchanged — the SSR renderer already injects these same translated
      # strings as HTML server-side.
      if /<\/?[a-zA-Z][^>]*>/.test(htmlContent) and translated.indexOf('{VAR}') is -1
        safeTranslation = sanitizeTranslationHtml(translated)
        return React.createElement(type, {...newProps, dangerouslySetInnerHTML: {__html: safeTranslation}})

      # Split the template on {VAR} to find static parts
      templateParts = htmlContent.split('{VAR}')

      # Collect variable values from children (strings, numbers, React elements).
      #
      # THE SLOTS ARE POSITIONAL. babelPlugin.js emits one {VAR} per expression
      # child, in source order, and the interleave below pairs {VAR} number i
      # with variableValues[i]. So a child that renders NOTHING still owns its
      # slot: dropping it shifts every later value one position left, and the
      # sentence silently reads the wrong number in the wrong place.
      #
      # That is not hypothetical. The org dashboard's averages line is
      #   "{VAR} {VAR} total teachers, and {VAR} total students. … is {VAR}."
      # where the first slot is a fragment rendered only for a district. On a
      # SCHOOL that child is undefined, so the school read "1 0 total teachers,
      # and N/A total students. … is ." — teachers in the district slot,
      # students in the teachers slot, playtime in the students slot, nothing
      # left for playtime — while the district, whose child is present, lined up
      # and looked perfect (card 6aa4d4091c031a8daf2e49c7). Every app on this
      # framework has the same exposure wherever a conditional sits inside a
      # translated sentence.
      #
      # null is the placeholder rather than '' because the interleave skips a
      # null value, so an absent child contributes nothing to the output — the
      # slot is held, not filled with an empty string.
      variableValues = []
      for child in children
        if child is ''
          # An EMPTY string is always an expression that rendered nothing
          # (`if cond then 'x' else ''`): JSX never produces '' from literal
          # text. Hold its slot exactly like null (card 6aa6dbae617b8dc32cc6b4c2).
          # Only the exact empty string: an inline ' ' or &nbsp; between two
          # expressions IS literal JSX text, serialized into the template.
          variableValues.push(null)
        else if typeof child == 'string'
          childText = child.trim()
          isInTemplate = templateParts.some((part) -> part.includes(child))
          variableValues.push(childText) if !isInTemplate && childText
        else if typeof child == 'number'
          variableValues.push(String(child))
        else if child?
          variableValues.push(child)
        else
          # A conditional child that rendered nothing. Hold its slot.
          variableValues.push(null)

      # Build a children array by splitting the translation on {VAR} and
      # interleaving the actual variable values — this correctly handles
      # React elements (e.g. <strong>) rather than dropping them with ''
      # Strip HTML tags from text parts since the React elements provide the actual markup
      translatedParts = translated.split('{VAR}').map (part) -> part.replace(/<[^>]+>/g, '')
      if translatedParts.length == 1
        # No {VAR} in translation — return as plain string (strip any HTML tags)
        return React.createElement(type, newProps, translatedParts[0])

      resultChildren = []
      for part, i in translatedParts
        resultChildren.push(part) if part
        resultChildren.push(variableValues[i]) if i < variableValues.length && variableValues[i]?

      return React.createElement(type, newProps, ...resultChildren)
    else
      # Register and render original
      window.i18nRegistry?.registerString(htmlContent)
      return React.createElement(type, newProps, ...children)

  # Don't process individual string children or props - they could be database values!
  # Only elements marked with ___i18n (static content) should be translated.
  # If we processed all string children/props, we'd translate dynamic data like:
  #   - Student names: <div>{student.first_name}</div>
  #   - Poset names: <h2>{poset.name}</h2>
  #   - Props: <TextField label={student.first_name} />
  #
  # For static props that need translation, developers should use t() helper:
  #   <TextField label={t("First Name")} />

  # Call React.createElement with original children and props
  React.createElement(type, props, ...children)

# Global translation helper function for programmatic strings
export t = (text) ->
  return text if !text?.trim?()

  trimmed = text.trim()

  # Compute hash from text only (no context)
  hash = md5(trimmed)

  # Check localStorage cache
  translated = window.i18nTranslationCache?[hash]

  if translated
    # Found in cache - return translation
    return translated
  else
    # Not in cache - register for future translation and return original
    window.i18nRegistry?.registerString(trimmed) if window.i18nRegistry?
    return trimmed
